'use client';

/**
 * Single chat message row — text, file attachments, tool calls/approvals, and
 * context blocks. Extracted from ChatMessageList so the list stays a thin
 * Virtuoso wrapper and this render logic can be unit-tested in isolation.
 *
 * ORDER MATTERS: everything except file attachments renders from the ordered
 * timeline built by `buildMessageTimeline`, so narration, tool runs, and the
 * final answer appear in the order they streamed. Do NOT reintroduce
 * render-by-type buckets here — that inverts the timeline, floating pre-tool
 * narration above the tool it introduced and making tool work read as a
 * footnote appended after the answer. Guarded by
 * `apps/hub/tests/meridian/chat-message-item-order.test.tsx`.
 *
 * WEIGHT ALSO MATTERS: the ordered segments are then arranged by
 * `buildTurnLayout` into (a) collapsible activity strips and (b) standalone
 * blocks — the final answer, approvals, artifacts, the plan. Order is untouched;
 * what changes is that the work no longer competes with the answer for the
 * reader's attention (a real turn produced 33 separately-framed blocks).
 *
 * The pieces live in `./chat-message-item/`:
 *  - `renderSegment.tsx`   — one ordered segment → its element (a FUNCTION, not
 *                            a component, so the rendered tree is unchanged)
 *  - `FileAttachment.tsx`  — one attachment card
 *  - `empty-response.ts`   — the blank-bubble guard, as a pure predicate
 */

import React from 'react';
import type { UIMessage } from 'ai';
import { MemoizedMarkdown } from '../MemoizedMarkdown.js';
import { TurnActivityBlock } from './TurnActivityBlock.js';
import { buildMessageTimeline, buildTurnLayout } from '../utils/messageTimeline.js';
import { CHAT_LAYOUT, AVATARS } from '../../styles/chat-layout.js';
import { FileAttachment } from './chat-message-item/FileAttachment.js';
import { formatModelLabel, readMessageModelId } from './chat-message-item/model-label.js';
import { renderSegment } from './chat-message-item/renderSegment.js';
import {
  EMPTY_RESPONSE_FALLBACK_TEXT,
  shouldShowEmptyResponseFallback,
} from './chat-message-item/empty-response.js';

export interface ChatMessageItemProps {
  message: UIMessage;
  messageIndex: number;
  isLastMessage: boolean;
  botAvatarUrl?: string;
  userInitials?: string;
  userAvatarUrl?: string;
  previewData: Record<string, Record<string, unknown> | undefined>;
  previewLoading: Record<string, boolean>;
  previewError: Record<string, boolean>;
  onFetchPreview: (
    toolName: string,
    toolInput: unknown,
    approvalId: string,
    apiBaseUrl: string
  ) => Promise<void>;
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
  isBusy: boolean;
  apiBaseUrl: string;
}

function ChatMessageItemComponent({
  message,
  messageIndex,
  isLastMessage,
  botAvatarUrl,
  userInitials,
  userAvatarUrl,
  previewData,
  previewLoading,
  previewError,
  onFetchPreview,
  onApprove,
  onDeny,
  isBusy,
  apiBaseUrl,
}: ChatMessageItemProps) {
  const isUser = message.role === 'user';
  const botAvatarEl = botAvatarUrl ? (
    <img src={botAvatarUrl} alt="Meridian" className={AVATARS.image} />
  ) : null;
  const userAvatarEl = userAvatarUrl ? (
    <img src={userAvatarUrl} alt="You" className={AVATARS.image} />
  ) : userInitials ? (
    <div className={AVATARS.initials}>{userInitials}</div>
  ) : null;

  // Memoized on the parts array. For completed messages the parts ref is stable,
  // so React.memo bails and this never recomputes; for the actively-streaming
  // last message parts change per token and it recomputes (unavoidable, and only
  // for that one row).
  const parts = message.parts;
  const timeline = React.useMemo(() => buildMessageTimeline(parts), [parts]);
  const { segments, fileParts, hasVisibleText, toolCount } = timeline;
  const blocks = React.useMemo(() => buildTurnLayout(segments), [segments]);

  const fileCount = fileParts.length;

  // Which model wrote this reply. Present only on assistant messages loaded
  // from the DB after attribution shipped; a live-streaming reply and every
  // older row render nothing rather than a guess.
  const modelId = isUser ? null : readMessageModelId(message.metadata);
  const modelLabel = formatModelLabel(modelId);

  const showEmptyResponseFallback = shouldShowEmptyResponseFallback({
    isUser,
    hasVisibleText,
    toolCount,
    segmentCount: segments.length,
    fileCount,
    isLastMessage,
    isBusy,
  });

  /**
   * Only the FINAL block of the actively-streaming message is live. A tool run
   * with text after it has, by definition, finished — so it collapses instead of
   * spinning, and the answer stays below it (no upward content jump).
   */
  const isStreamingRow = isLastMessage && isBusy;
  const lastBlockIndex = blocks.length - 1;

  const segmentContext = {
    messageId: message.id,
    messageIndex,
    isUser,
    previewData,
    previewLoading,
    previewError,
    onFetchPreview,
    onApprove,
    onDeny,
    isBusy,
    apiBaseUrl,
  };

  return (
    <div className="w-full">
      <div
        key={`${message.id}-${messageIndex}`}
        className={`${CHAT_LAYOUT.messageItem} ${
          isUser ? CHAT_LAYOUT.messageItemUser : CHAT_LAYOUT.messageItemBot
        }`}
      >
        {!isUser && <div className="flex-shrink-0 pt-1">{botAvatarEl}</div>}
        <div
          className={`${CHAT_LAYOUT.messageBubble} ${
            isUser ? CHAT_LAYOUT.messageBubbleUser : CHAT_LAYOUT.messageBubbleBot
          } ${!isUser ? 'px-6 py-3' : ''}`}
        >
          {blocks.map((block, index) => {
            const isLive = isStreamingRow && index === lastBlockIndex;
            if (block.kind === 'activity') {
              return (
                <TurnActivityBlock
                  key={block.key}
                  segments={block.segments}
                  isStreaming={isLive}
                  idPrefix={`${message.id}-${messageIndex}`}
                />
              );
            }
            return renderSegment(block.segment, index, isLive, segmentContext);
          })}

          {/* Empty-response fallback — a finished assistant turn that said
              nothing renders this instead of a blank bubble. */}
          {showEmptyResponseFallback && (
            <div className="max-w-none text-xs md:text-sm text-gray-500 dark:text-gray-400 italic">
              <MemoizedMarkdown
                id={`${message.id}-${messageIndex}-empty-fallback`}
                content={EMPTY_RESPONSE_FALLBACK_TEXT}
              />
            </div>
          )}

          {/* File attachments live outside the timeline: the AI SDK orders a
              user's file parts BEFORE their text part, so timelining them would
              render the attachment cards above the message they belong to. */}
          {isUser && fileCount > 0 && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 font-medium">
              📎 Shared {fileCount} file{fileCount !== 1 ? 's' : ''}
            </div>
          )}
          {fileParts.map((file, i) => (
            <FileAttachment key={`${message.id}-${messageIndex}-file-${i}`} file={file} />
          ))}

          {/* Model caption. Quiet by design (MARKETING_MINIMALISM): it answers
              "which model said this" on inspection without competing with the
              answer. `title` carries the exact id the caption shortens. */}
          {modelLabel && (
            <div
              className="mt-2 text-[11px] leading-none text-gray-400 dark:text-gray-500 select-none"
              title={modelId ?? undefined}
            >
              {modelLabel}
            </div>
          )}
        </div>
        {isUser && userAvatarEl}
      </div>
    </div>
  );
}

/**
 * Memoized so that during streaming only the actively-changing last message
 * re-renders. Completed messages keep a stable `message` object ref (from
 * useChat) and stable handler/preview props, so React.memo skips rebuilding
 * their timeline on every streamed token.
 */
export const ChatMessageItem = React.memo(ChatMessageItemComponent);
